import React, { useEffect, useRef } from 'react';
import { Navigate, Routes, Route, useLocation, useParams } from 'react-router-dom';
import Homepage from './screens/Homepage';
import EmployeeSettingsPage from './screens/Admin/Settings';
import { AuthProvider } from './contexts/AuthContext';
import EmployeeOnlyRoute from './routes/EmployeeOnlyRoute';
import EmployeeAppointmentPage from './screens/EmployeeAppointmentPage';
import MyAppointmentsScreen from './screens/Admin/MyAppointmentsScreen';
import ServicesScreen from './screens/Admin/ServicesScreen';
import AddEmployeePage from './screens/Admin/AddEmployeePage';
import AdminDashboard from './screens/Admin/AdminDashboard/AdminDashboard';
import AdminLogin from './screens/Admin/AdminLogin';
import EditWorkHoursPage from './screens/Admin/EditWorkhours/EditWorkHoursPage';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import NotFoundPage from './screens/Admin/NotFound';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SignUpPage from './screens/Admin/SignUpPage';
import AdminOnlyRoute from './routes/AdminOnlyRoute';
import MyProfileScreen from './screens/Admin/MyProfilePage/MyProfilePage';
import TimezoneInitializer from './components/global/TimezoneInitializer';
import CustomerAppointmentPage from './screens/CustomerAppointmentPage';
import SuperAdminOnlyRoute from './routes/SuperAdminOnlyRoute';
import SuperAdminLayout from './screens/SuperAdmin/SuperAdminLayout';
import ManageSitePage from './screens/SuperAdmin/ManageSitePage';
import SuperAdminDashboard from './screens/SuperAdmin/SuperAdminDashboard';
import EditBusinessConfigPage from './screens/SuperAdmin/BusinessConfigPage';
import SuperAdminAddEmployeePage from './screens/SuperAdmin/AddEmployee';
import SuperAdminEditServicesPage from './screens/SuperAdmin/EditServices';
import SuperAdminAnalyticsPage from './screens/SuperAdmin/SuperAdminAnalyticsPage';
import OwnerOnlyRoute from './routes/OwnerOnlyRoute';
import ManageSubscriptionPage from './screens/Admin/ManageSubscription/ManageSubscriptionPage';
import MissingSubscriptionPage from './screens/Admin/ManageSubscription/MissingSubscriptionPage';
import ResetPasswordPage from './screens/Admin/ResetPasswordPage';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import PaymentPortalPage from './screens/PaymentPortalPage';
import ScheduleHub from './screens/Admin/ScheduleHub';
import ContactSupportPage from './screens/Admin/ContactSupportPage';
import { AdminDataProvider } from './contexts/AdminDataContext';
import AnalyticsPage from './screens/Admin/Analytics/AnalyticsPage';
import CustomersPage from './screens/Admin/Customers/CustomersPage';
import ManageEmployeesPage from './screens/Admin/ManageEmployeesPage';
import SitePublishingWorkspace from './screens/Admin/SitePublishing/SitePublishingWorkspace';
import { TenantQueryController } from './tenancy/TenantQueryController';
import { TenantThemeProvider } from './theme/TenantThemeProvider';
import { useOptionalTenant } from './tenancy/TenantContext';
import ProvisionTenantPage from './screens/SuperAdmin/ProvisionTenantPage';
import {AuthQueryCacheController} from './auth/AuthQueryCacheController';
import OnboardingPage from './screens/Admin/Onboarding/OnboardingPage';

const TenantSiteBuilderRedirect: React.FC<{ superAdmin?: boolean; section?: string }> = ({ superAdmin, section }) => {
  const tenantId = useOptionalTenant()?.tenantId;
  const { siteId } = useParams();
  const selectedTenant = siteId || tenantId;
  if (!selectedTenant) return <Navigate replace to="/admin/superadmin" />;
  const path = superAdmin ? '/admin/superadmin/site' : '/admin/site';
  return <Navigate replace to={`${path}?tenant=${encodeURIComponent(selectedTenant)}${section ? `#${section}` : ''}`} />;
};

const ManifestController: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const manifestLink = document.getElementById('app-manifest');

    if (!(manifestLink instanceof HTMLLinkElement)) {
      return;
    }

    const manifestHref = location.pathname.startsWith('/admin')
      ? `${process.env.PUBLIC_URL}/admin.webmanifest`
      : `${process.env.PUBLIC_URL}/manifest.json`;

    if (manifestLink.href !== new URL(manifestHref, window.location.origin).href) {
      manifestLink.href = manifestHref;
    }
  }, [location.pathname]);

  return null;
};

const App: React.FC = () => {
  const queryClientRef = useRef<QueryClient>();
  if (!queryClientRef.current) {
    queryClientRef.current = new QueryClient({
      defaultOptions: { queries: { staleTime: 1000 * 60 * 20 } },
    });
    queryClientRef.current.setQueryDefaults(['currentEmployee'], {
      staleTime: 1000 * 60 * 5,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });
    queryClientRef.current.setQueryDefaults(['appointment'], {
      gcTime: Infinity, // never evict unless you explicitly remove it
    });
  }
  const queryClient = queryClientRef.current;

  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);


  return (
    <Elements stripe={stripePromise}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <AuthProvider>
          <QueryClientProvider client={queryClient}>
            <AuthQueryCacheController />
            <TimezoneInitializer />
            <ManifestController />
            <TenantQueryController />
            <TenantThemeProvider>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Homepage />} />
                <Route path="/book-now" element={<Homepage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />

                {/* Admin entry + protected routes */}
                <Route path="admin">
                  <Route index element={<AdminLogin />} />
                  <Route

                    element={
                      <EmployeeOnlyRoute>
                        <AdminDataProvider>
                          <AdminDashboard />
                        </AdminDataProvider>
                      </EmployeeOnlyRoute>
                    }
                  >
                    {/* default dashboard view */}
                    <Route path="dashboard" element={<ScheduleHub />} />
                    <Route index element={<ScheduleHub />} />

                    {/* Only admins can access these routes */}
                    <Route path="manage-services" element={<AdminOnlyRoute element={<ServicesScreen />} />} />
                    <Route path="website-details" element={<AdminOnlyRoute element={<TenantSiteBuilderRedirect section="business" />} />} />
                    <Route path="add-employee" element={<AdminOnlyRoute element={<AddEmployeePage />} />} />
                    {/* <Route path="business-config" element={<AdminOnlyRoute element={<BusinessConfigForm />} />} /> */}
                    <Route path="gallery" element={<AdminOnlyRoute element={<TenantSiteBuilderRedirect section="gallery" />} />} />

                    {/* Employee routes */}
                    <Route path="edit-work-hours" element={<EditWorkHoursPage />} />
                    {/* <Route path="manage-schedule" element={<ManageSchedule />} /> */}
                    <Route path="appointments" element={<MyAppointmentsScreen />} />
                    <Route path="analytics" element={<AnalyticsPage />} />
                    <Route path="customers" element={<CustomersPage />} />
                    <Route path="settings" element={<EmployeeSettingsPage />} />
                    <Route path="contact-support" element={<ContactSupportPage />} />
                    <Route path="payment-portal" element={<PaymentPortalPage />} />
                    <Route path="manage-employees" element={<AdminOnlyRoute element={<ManageEmployeesPage />} />} />
                    <Route path="site" element={<AdminOnlyRoute element={<SitePublishingWorkspace />} />} />
                    <Route path="onboarding" element={<OwnerOnlyRoute element={<OnboardingPage />} />} />

                    <Route path="my-profile" element={<MyProfileScreen />} />
                    {/* <Route path="my-calendar" element={<CalendarScreen />} /> */}
                    <Route path="schedule-hub" element={<ScheduleHub />} />
                    <Route path="missing-subscription" element={<MissingSubscriptionPage />} />

                    {/* Owner Routes */}
                    <Route path="manage-subscription" element={<OwnerOnlyRoute element={<ManageSubscriptionPage />} />} />
                    <Route path=":appointmentId" element={<EmployeeAppointmentPage />} />
                  </Route>

                  {/* Founder tools do not require a tenant employee record. */}
                  <Route path="superadmin" element={<SuperAdminOnlyRoute element={<SuperAdminLayout />} />}>
                    <Route index element={<SuperAdminDashboard />} />
                    <Route path="provision" element={<ProvisionTenantPage />} />
                    <Route path="manage/:siteId" element={<ManageSitePage />} />
                    <Route path="analytics" element={<SuperAdminAnalyticsPage />} />
                    <Route path="manage/:siteId/businessConfig" element={<EditBusinessConfigPage />} />
                    <Route path="manage/:siteId/add-employee" element={<SuperAdminAddEmployeePage />} />
                    <Route path="manage/:siteId/manage-services" element={<SuperAdminEditServicesPage />} />
                  </Route>
                  {/* The Site Builder is a full-screen workspace with its own toolbar. */}
                  <Route path="superadmin/site" element={<SuperAdminOnlyRoute element={<SitePublishingWorkspace />} />} />
                  <Route path="superadmin/manage/:siteId/homepage-reviews"
                    element={<SuperAdminOnlyRoute element={<TenantSiteBuilderRedirect superAdmin section="reviews" />} />} />
                </Route>


                {/* Signup & Catch-All */}
                <Route path="MyAppointment" element={<CustomerAppointmentPage />} />
                <Route path="SignUp" element={<SignUpPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes >
            </TenantThemeProvider>
            <ReactQueryDevtools />
          </QueryClientProvider>

        </AuthProvider >
      </LocalizationProvider >
    </Elements >
  );
};

export default App;
